// UI-2 Layers panel: swatch, rename, visible/lock/print, active layer, drag reorder,
// isolate, opacity, delete-with-migrate.
import { useState } from "preact/hooks";
import type { AppApi } from "../appApi";
import type { ToolManager } from "../tools/toolManager";
import type { Layer } from "../doc/entities";
import {
  makeAddLayerCommand,
  makeDeleteLayerCommand,
  makeReorderLayerCommand,
  makeUpdateLayerCommand
} from "../doc/layers";

export function LayersPanel({ api, tools }: { api: AppApi; tools: ToolManager }) {
  const { store } = api;
  const doc = store.doc;
  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [migrateTarget, setMigrateTarget] = useState<string>("");
  const [isolateRestore, setIsolateRestore] = useState<Map<string, boolean> | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  const update = (id: string, changes: Partial<Layer>) =>
    store.dispatch(makeUpdateLayerCommand(doc, id, changes));

  const toggleIsolate = () => {
    if (isolateRestore) {
      // restore previous visibility map (EC-UI-4: stale entries are simply re-applied)
      const commands = doc.layers
        .filter((l) => isolateRestore.has(l.id) && isolateRestore.get(l.id) !== l.visible)
        .map((l) => makeUpdateLayerCommand(doc, l.id, { visible: isolateRestore.get(l.id)! }));
      if (commands.length > 0) store.dispatch({ kind: "batch", label: "unisolate", commands });
      setIsolateRestore(null);
    } else {
      const map = new Map(doc.layers.map((l) => [l.id, l.visible]));
      const active = tools.activeLayerId;
      const commands = doc.layers
        .filter((l) => l.visible !== (l.id === active))
        .map((l) => makeUpdateLayerCommand(doc, l.id, { visible: l.id === active }));
      if (commands.length > 0) store.dispatch({ kind: "batch", label: "isolate", commands });
      setIsolateRestore(map);
    }
  };

  const confirmDelete = (moveTo: string | null) => {
    if (!deleting) return;
    store.dispatch(makeDeleteLayerCommand(doc, deleting, moveTo));
    setDeleting(null);
  };

  const requestDelete = (id: string) => {
    if (doc.layers.length <= 1) return; // EC-UI-3
    if ((doc.zOrder[id] ?? []).length === 0) {
      store.dispatch(makeDeleteLayerCommand(doc, id, null));
      return;
    }
    const firstOther = doc.layers.find((l) => l.id !== id)!;
    setMigrateTarget(firstOther.id);
    setDeleting(id);
  };

  const onDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const to = doc.layers.findIndex((l) => l.id === targetId);
    store.dispatch(makeReorderLayerCommand(doc, dragId, to));
    setDragId(null);
  };

  const activeLayer = doc.layers.find((l) => l.id === tools.activeLayerId);
  const rows = [...doc.layers].reverse(); // topmost layer first, CAD-style

  return (
    <div class="layers-panel">
      {rows.map((l) => (
        <div
          key={l.id}
          class={`layer-row${l.id === tools.activeLayerId ? " active" : ""}`}
          draggable
          onDragStart={() => setDragId(l.id)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => onDrop(l.id)}
          onClick={() => tools.setActiveLayer(l.id)}
        >
          <input
            type="color"
            class="layer-swatch"
            value={l.color}
            title="Layer color"
            aria-label={`${l.name} layer color`}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => update(l.id, { color: (e.target as HTMLInputElement).value })}
          />
          {renaming === l.id ? (
            <input
              class="layer-name-input"
              value={l.name}
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  update(l.id, { name: (e.target as HTMLInputElement).value.trim() || l.name });
                  setRenaming(null);
                }
                if (e.key === "Escape") setRenaming(null);
              }}
              onBlur={(e) => {
                update(l.id, { name: (e.target as HTMLInputElement).value.trim() || l.name });
                setRenaming(null);
              }}
            />
          ) : (
            <span class="layer-name" onDblClick={() => setRenaming(l.id)}>
              {l.name}
            </span>
          )}
          <button
            class={`layer-toggle${l.visible ? " on" : ""}`}
            title="Visible"
            onClick={(e) => {
              e.stopPropagation();
              update(l.id, { visible: !l.visible });
            }}
          >
            👁
          </button>
          <button
            class={`layer-toggle${l.locked ? " on" : ""}`}
            title="Locked"
            onClick={(e) => {
              e.stopPropagation();
              update(l.id, { locked: !l.locked });
            }}
          >
            🔒
          </button>
          <button
            class={`layer-toggle${l.print ? " on" : ""}`}
            title="Include in print/export"
            onClick={(e) => {
              e.stopPropagation();
              update(l.id, { print: !l.print });
            }}
          >
            🖨
          </button>
        </div>
      ))}

      {deleting && (
        <div class="layer-migrate">
          <div>Move {(doc.zOrder[deleting] ?? []).length} objects to:</div>
          <select value={migrateTarget} onChange={(e) => setMigrateTarget((e.target as HTMLSelectElement).value)}>
            {doc.layers
              .filter((l) => l.id !== deleting)
              .map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
          </select>
          <div class="layer-migrate-actions">
            <button onClick={() => confirmDelete(migrateTarget)}>Move &amp; delete layer</button>
            <button class="danger" onClick={() => confirmDelete(null)}>
              Delete objects too
            </button>
            <button onClick={() => setDeleting(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div class="layer-footer">
        <button
          id="layer-add"
          title="Add a layer. The new layer becomes the active layer: new and pasted objects go on it."
          onClick={() => {
            // 2026-10-20: the new layer was not made active, so a paste after "add layer" went on the OLD layer, and
            // hiding the new layer hid nothing.
            const cmd = makeAddLayerCommand(doc);
            store.dispatch(cmd);
            if (cmd.kind === "addLayer") tools.setActiveLayer(cmd.layer.id);
          }}
        >
          ＋
        </button>
        <button
          title="Delete active layer"
          disabled={doc.layers.length <= 1}
          onClick={() => requestDelete(tools.activeLayerId)}
        >
          🗑
        </button>
        <button
          title="Isolate active layer (solo)"
          class={isolateRestore ? "on" : ""}
          onClick={toggleIsolate}
        >
          ◎
        </button>
        {activeLayer && (
          <label class="layer-opacity" title="Active layer opacity">
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(activeLayer.opacity * 100)}
              onChange={(e) =>
                update(activeLayer.id, { opacity: Number((e.target as HTMLInputElement).value) / 100 })
              }
            />
            <span class="layer-opacity-value">{Math.round(activeLayer.opacity * 100)}%</span>
          </label>
        )}
      </div>
    </div>
  );
}
